import { authFetch } from '../../../utils/authFetch';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { TemplateSelector } from '../../../components/TemplateSelector';
import { TemplatePreview } from '../../../components/TemplatePreview';
import { CapabilityTemplate } from '../../../types/api';
import { ApplicationAccessEditor } from '../../../components/access/ApplicationAccessEditor';
import {
  AccessDraft,
  AssignableAppsResponse,
  CatalogApp,
  MainRole,
  appScopes,
  overrideScopes,
  scopeDelta,
  tierForMainRole,
} from '../../../components/access/accessModel';

interface Member {
  id: string;
  userId: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  status: 'active' | 'pending';
  type: 'member' | 'invite';
  inviteId?: string;
  expiresAt?: string;
  templateId?: string | null;
  templateName?: string | null;
  /** Owner/admin callers only — absent means "not visible to you". */
  appRoles?: Record<string, string>;
  scopes?: string[];
  scopeCountByApp?: Record<string, number>;
  appRolesSource?: string | null;
  takesSeat?: boolean;
}

interface SeatsInfo {
  current: number;
  included: number;
  additional: number;
  perSeatCents: number;
}

/**
 * GET /api/workspaces/:id/seats — seatService.getSeatInfo verbatim. This is
 * the figure invite acceptance enforces, so it is shown as-is and never
 * recomputed from the member list.
 */
interface HouseholdSeats {
  seatLimit: number;
  activeSeats: number;
  availableSeats: number;
  isAtLimit: boolean;
}

/** GET /api/workspaces/:id/people — the wire shape. */
interface PeopleResponse {
  members?: Array<{
    userId: string;
    email: string;
    name: string | null;
    avatarUrl: string | null;
    role: Member['role'];
    roleLabel?: string | null;
    appRoles?: Record<string, string>;
    templateId?: string | null;
    scopes?: string[];
    scopeCountByApp?: Record<string, number>;
  }>;
  invites?: Array<{
    inviteId: string;
    email: string;
    role: Member['role'];
    roleLabel?: string | null;
    expiresAt: string;
    templateId?: string | null;
    appRoles?: Record<string, string>;
    appRolesSource?: string | null;
    scopeCountByApp?: Record<string, number>;
    takesSeat?: boolean;
  }>;
}

/**
 * One list for the tab. Member rows are keyed by userId because that is what
 * PATCH/DELETE /members/:memberId resolve; invite rows by inviteId.
 */
function toMemberRows(data: PeopleResponse): Member[] {
  const members: Member[] = (data.members ?? []).map((m) => ({
    id: m.userId,
    userId: m.userId,
    email: m.email,
    name: m.name ?? '',
    avatarUrl: m.avatarUrl ?? null,
    role: m.role,
    status: 'active',
    type: 'member',
    templateName: m.roleLabel ?? null,
    templateId: m.templateId ?? null,
    appRoles: m.appRoles,
    scopes: m.scopes,
    scopeCountByApp: m.scopeCountByApp,
  }));
  const invites: Member[] = (data.invites ?? []).map((i) => ({
    id: i.inviteId,
    userId: '',
    email: i.email,
    name: '',
    avatarUrl: null,
    role: i.role,
    status: 'pending',
    type: 'invite',
    inviteId: i.inviteId,
    expiresAt: i.expiresAt,
    templateName: i.roleLabel ?? null,
    templateId: i.templateId ?? null,
    appRoles: i.appRoles,
    appRolesSource: i.appRolesSource,
    scopeCountByApp: i.scopeCountByApp,
    takesSeat: i.takesSeat,
  }));
  return [...members, ...invites];
}

function seatLimitMessage(isPersonal: boolean): string {
  return isPersonal
    ? 'Your household has no free seats. Upgrade your plan or remove a member before this person can join.'
    : 'This workspace has reached its seat limit. Upgrade the plan or remove a member before this person can join.';
}

interface MembersTabProps {
  workspaceId: string;
  authToken: string;
  workspaceType: 'personal' | 'team' | 'enterprise';
  /**
   * The caller's DB role in this workspace (GET /api/workspaces/:id →
   * membership.role). Read by the tab itself when omitted. Unknown → no
   * management controls (fail closed).
   */
  callerRole?: string | null;
  /** The caller's user id, so their own row offers nothing Central refuses. */
  callerUserId?: string | null;
}

type InvitableRole = 'admin' | 'member' | 'viewer';

/**
 * What the caller may do, mirroring Central's rules exactly (decision D1), so
 * the tab never offers an action Central refuses:
 *
 *   invite             owner/admin; an admin cannot invite an admin
 *                      (ADMIN_CANNOT_INVITE_ADMIN)
 *   change role        OWNER ONLY (ROLE_PERMISSION_REQUIRED); never on the
 *                      owner's row (OWNER_ROLE_IMMUTABLE) or your own
 *                      (CANNOT_CHANGE_OWN_ROLE); never TO owner (that is
 *                      transfer ownership, owner-only, in General)
 *   remove             owner/admin; never the owner (OWNER_NOT_REMOVABLE) or
 *                      yourself (CANNOT_REMOVE_SELF); an admin cannot remove an
 *                      admin (ADMIN_CANNOT_REMOVE_ADMIN)
 *   app access         owner/admin; the owner's row only in a personal
 *                      household (OWNER_APP_ROLES_IMMUTABLE elsewhere); never
 *                      your own row (Central lets you only LOWER your own
 *                      access — SCOPE_AUTHORITY / APP_ROLE_AUTHORITY — so,
 *                      like Shared, the tab offers no self-edit at all)
 *   permissions        owner/admin, never the owner's row or your own
 *                      (business only)
 *   invites            owner/admin may resend and cancel
 */
export interface MemberActionPolicy {
  canInvite: boolean;
  inviteRoles: InvitableRole[];
  canManageInvites: boolean;
  canChangeRole: (target: { userId: string; role: string }) => boolean;
  canRemove: (target: { userId: string; role: string }) => boolean;
  canEditAppAccess: (target: { userId: string; role: string }, isPersonal: boolean) => boolean;
  canEditPermissions: (target: { userId: string; role: string }) => boolean;
}

export function memberActionPolicy(callerRole: string | null | undefined, callerUserId: string | null | undefined): MemberActionPolicy {
  const owner = callerRole === 'owner';
  const manager = owner || callerRole === 'admin';
  const self = (t: { userId: string }) => !!callerUserId && t.userId === callerUserId;
  return {
    canInvite: manager,
    inviteRoles: owner ? ['admin', 'member', 'viewer'] : manager ? ['member', 'viewer'] : [],
    canManageInvites: manager,
    canChangeRole: (t) => owner && t.role !== 'owner' && !self(t),
    canRemove: (t) => manager && t.role !== 'owner' && !self(t) && !(callerRole === 'admin' && t.role === 'admin'),
    canEditAppAccess: (t, isPersonal) => manager && !self(t) && (t.role !== 'owner' || isPersonal),
    canEditPermissions: (t) => manager && t.role !== 'owner' && !self(t),
  };
}

/** Central's access-profile preview verdict (`authority`), as words for a toast. */
export function profileRefusalMessage(authority: {
  code: string | null;
  appRoleViolations?: Array<{ appId: string; required: string }>;
  scopeViolations?: Array<{ capability: string }>;
}): string {
  if (authority.code === 'OWNER_ACCESS_IMMUTABLE') return 'The workspace owner’s access follows ownership and can’t be changed here';
  if (authority.code === 'APP_ROLE_AUTHORITY') {
    const apps = (authority.appRoleViolations ?? []).map((v) => `${v.appId} (${v.required})`).join(', ');
    return `This subrole grants application access above your own: ${apps}`;
  }
  const caps = (authority.scopeViolations ?? []).map((v) => v.capability);
  const shown = caps.slice(0, 5).join(', ') + (caps.length > 5 ? `, +${caps.length - 5} more` : '');
  return `This subrole grants permissions above your own: ${shown}`;
}

const APP_ROLE_TIER: Record<string, number> = { viewer: 1, member: 2, admin: 3 };

export interface AppRoleAuthorityViolation {
  appId: string;
  required: string;
  /** The caller's own effective tier for that app (null = no access). */
  callerHas: string | null;
}

/**
 * Central's app-roles authority rule, mirrored (PATCH …/app-roles →
 * 403 APP_ROLE_AUTHORITY, `computeTemplateAuthorityViolations`): an owner may
 * grant anything; anyone else may only write a map whose every app is at or
 * below their OWN effective tier (`appRoles[app] ?? appRoles['*']`). The whole
 * submitted map is checked, untouched apps included.
 *
 * Self-edits by an admin are therefore ALLOWED by Central but can only keep or
 * lower their own access; raising it, or adding an app they do not hold, is
 * refused — and only the owner can give it back.
 */
export function appRoleAuthorityViolations(
  callerRole: string | null | undefined,
  callerAppRoles: Record<string, string> | null | undefined,
  desired: Record<string, string>,
): AppRoleAuthorityViolation[] {
  if (callerRole === 'owner') return [];
  const out: AppRoleAuthorityViolation[] = [];
  for (const [appId, required] of Object.entries(desired)) {
    const has = callerAppRoles ? callerAppRoles[appId] ?? callerAppRoles['*'] ?? null : null;
    if (!has || (APP_ROLE_TIER[has] ?? 0) < (APP_ROLE_TIER[required] ?? 99)) {
      out.push({ appId, required, callerHas: has });
    }
  }
  return out;
}

/** The full map the editor will PATCH: listed apps from the draft, untouched/dormant keys unchanged. */
export function nextAppRolesFor(
  stored: Record<string, string> | undefined,
  draftAppRoles: Record<string, string>,
  listedAppIds: Iterable<string>,
): Record<string, string> {
  const next: Record<string, string> = { ...(stored ?? {}) };
  for (const id of listedAppIds) {
    if (id in draftAppRoles) next[id] = draftAppRoles[id]!;
    else delete next[id];
  }
  return next;
}

/** A destructive or authority-changing action waiting for the caller to confirm it. */
type PendingConfirm =
  | { kind: 'remove'; member: Member }
  | { kind: 'role'; member: Member; newRole: InvitableRole }
  | { kind: 'cancel-invite'; member: Member };

const roleLabels: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

const roleColors: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-emerald-100 text-emerald-700',
  member: 'bg-gray-100 text-gray-700',
  viewer: 'bg-gray-100 text-gray-500',
};

/**
 * Members settings tab
 *
 * Features:
 * - Member list with roles
 * - Pending invites
 * - Invite new members
 * - Remove/resend/cancel invites
 * - Seat count and pricing summary
 */
export function MembersTab({ workspaceId, workspaceType, authToken, callerRole, callerUserId }: MembersTabProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [seats, setSeats] = useState<SeatsInfo | null>(null);
  const [householdSeats, setHouseholdSeats] = useState<HouseholdSeats | null>(null);
  const isPersonal = workspaceType === 'personal';
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [inviteTemplate, setInviteTemplate] = useState<CapabilityTemplate | null>(null);
  const [inviteStep, setInviteStep] = useState<'details' | 'permissions'>('details');
  const [isInviting, setIsInviting] = useState(false);
  const [actionMemberId, setActionMemberId] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [editingAccess, setEditingAccess] = useState<Member | null>(null);
  const access = useAccessData(workspaceId, authToken);
  /**
   * The invite's application access. `dirty` is false until the inviter
   * changes something: an untouched invite sends NO selection, so Central
   * applies its own default (Home for a household, the Subrole's apps for a
   * business) — the same thing the editor pre-shows.
   */
  const [inviteAccess, setInviteAccess] = useState<{ draft: AccessDraft; dirty: boolean } | null>(null);
  const [resolvedRole, setResolvedRole] = useState<string | null | undefined>(callerRole);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);
  const policy = memberActionPolicy(resolvedRole, callerUserId);

  useEffect(() => {
    loadData();
  }, [workspaceId]);

  // The caller's role comes from the page; when it is not given, one read of
  // the workspace answers it. Until known, nothing is offered (fail closed).
  useEffect(() => {
    if (callerRole !== undefined) {
      setResolvedRole(callerRole);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/workspaces/${workspaceId}`, authToken);
        const data = res.ok ? await res.json() : null;
        if (!cancelled) setResolvedRole(typeof data?.membership?.role === 'string' ? data.membership.role : null);
      } catch {
        if (!cancelled) setResolvedRole(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, authToken, callerRole]);

  // An admin cannot invite an admin: keep the selected role one Central accepts.
  useEffect(() => {
    if (policy.inviteRoles.length > 0 && !policy.inviteRoles.includes(inviteRole)) {
      setInviteRole('member');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedRole]);

  async function confirmPending() {
    const p = pendingConfirm;
    if (!p) return;
    if (p.kind === 'remove') await handleRemoveMember(p.member.id);
    else if (p.kind === 'role') await handleChangeRole(p.member.id, p.newRole);
    else await handleCancelInvite(p.member.inviteId!);
    setPendingConfirm(null);
  }

  useEffect(() => {
    if (showInviteModal) access.ensureLoaded();
  }, [showInviteModal]);

  // Seed the invite's application access from Central's defaults once the app
  // list is known: a household pre-selects Home at the main role's tier.
  // Re-seeded on a main-role change until the inviter edits it themselves.
  useEffect(() => {
    if (!showInviteModal || !access.data) return;
    setInviteAccess((prev) => {
      if (prev?.dirty) {
        const tier = tierForMainRole(inviteRole);
        const appRoles: Record<string, string> = {};
        for (const k of Object.keys(prev.draft.appRoles)) appRoles[k] = tier;
        return { draft: { ...prev.draft, appRoles }, dirty: true };
      }
      const appRoles: Record<string, string> = {};
      const pd = access.data!.assignable.personalDefaults;
      if (pd) appRoles[pd.appId] = pd.byMainRole[inviteRole];
      return { draft: { appRoles, overrides: {} }, dirty: false };
    });
  }, [showInviteModal, access.data, inviteRole]);

  async function loadData() {
    try {
      // A personal household reads seat usage from seat enforcement itself;
      // a business workspace keeps its billing seat summary.
      const [peopleRes, seatsRes] = await Promise.all([
        authFetch(`/api/workspaces/${workspaceId}/people`, authToken),
        isPersonal
          ? authFetch(`/api/workspaces/${workspaceId}/seats`, authToken)
          : authFetch(`/api/workspaces/${workspaceId}/pricing`, authToken),
      ]);

      if (peopleRes.ok) {
        const data = (await peopleRes.json()) as PeopleResponse;
        setMembers(toMemberRows(data));
      }

      if (seatsRes.ok) {
        const data = await seatsRes.json();
        if (isPersonal) {
          setHouseholdSeats(data.seats || null);
        } else {
          setSeats(data.pricing?.seats || null);
        }
      }
    } catch {
      toast.error('Failed to load members');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleInvite() {
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(buildInvitePayload()),
      });

      if (response.ok) {
        toast.success('Invitation sent');
        closeInviteModal();
        loadData();
      } else {
        const error = await response.json();
        toast.error(
          error.code === 'SEAT_LIMIT_EXCEEDED'
            ? seatLimitMessage(isPersonal)
            : error.error || 'Failed to send invitation'
        );
      }
    } catch {
      toast.error('Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  }

  /**
   * The invite request. Application access is sent only when the inviter
   * changed it; per-app customizations travel as `scopes`. Central cannot
   * combine a Subrole with a narrowed scope list (`templateId` wins), so a
   * customized invite is sent as explicit scopes and the preview says so.
   */
  function buildInvitePayload(): Record<string, unknown> {
    const body: Record<string, unknown> = {
      email: inviteEmail.trim(),
      role: inviteRole,
      templateId: inviteTemplate?.id || null,
    };
    if (inviteAccess?.dirty) {
      const selected = Object.keys(inviteAccess.draft.appRoles).sort();
      body.appRoles = inviteAccess.draft.appRoles;
      body.selectedAppIds = selected;
      const scopes = overrideScopes(inviteAccess.draft);
      const customized = Object.entries(inviteAccess.draft.overrides).some(
        ([appId, list]) => list !== null && appId in inviteAccess.draft.appRoles,
      );
      if (customized) {
        body.scopes = scopes;
        body.templateId = null;
      }
    }
    return body;
  }

  function closeInviteModal() {
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('member');
    setInviteTemplate(null);
    setInviteStep('details');
    setInviteAccess(null);
  }

  /**
   * Apply a Subrole to an existing member through Central's access-profile
   * flow: preview (which returns the fingerprint of exactly what will be
   * applied), then apply against that fingerprint. The member PATCH this used
   * to call changes the main role only and rejected a template-only body.
   */
  async function handleUpdateMemberTemplate(memberId: string, templateId: string | null) {
    if (!templateId) return;
    setActionMemberId(memberId);
    try {
      const preview = await authFetch(
        `/api/workspaces/${workspaceId}/members/${memberId}/access-profile/preview`,
        authToken,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ templateId }),
        },
      );
      if (!preview.ok) {
        const error = await preview.json();
        toast.error(error.error || 'Failed to update permissions');
        return;
      }
      const { fingerprint, authority } = await preview.json();
      // Central's preview says whether it would refuse THIS caller (owner's row,
      // above the caller's own tier or permissions); stop here instead of
      // letting the apply fail.
      if (authority && authority.ok === false) {
        toast.error(profileRefusalMessage(authority));
        return;
      }
      const response = await authFetch(`/api/workspaces/${workspaceId}/members/${memberId}/access-profile`, authToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateId, expectedFingerprint: fingerprint, reason: 'Subrole applied from Central settings' }),
      });

      if (response.ok) {
        toast.success('Permissions updated');
        setEditingMember(null);
        loadData();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update permissions');
      }
    } catch {
      toast.error('Failed to update permissions');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleRemoveMember(memberId: string) {
    setActionMemberId(memberId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members/${memberId}`, authToken, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Member removed');
        setMembers((prev) => prev.filter((m) => m.id !== memberId));
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to remove member');
      }
    } catch {
      toast.error('Failed to remove member');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleResendInvite(inviteId: string) {
    setActionMemberId(inviteId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/invites/${inviteId}/resend`, authToken, {
        method: 'POST',
        
      });

      if (response.ok) {
        toast.success('Invitation resent');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to resend invitation');
      }
    } catch {
      toast.error('Failed to resend invitation');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleCancelInvite(inviteId: string) {
    setActionMemberId(inviteId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/invites/${inviteId}`, authToken, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Invitation cancelled');
        setMembers((prev) => prev.filter((m) => m.inviteId !== inviteId));
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to cancel invitation');
      }
    } catch {
      toast.error('Failed to cancel invitation');
    } finally {
      setActionMemberId(null);
    }
  }

  async function handleChangeRole(memberId: string, newRole: string) {
    setActionMemberId(memberId);
    try {
      const response = await authFetch(`/api/workspaces/${workspaceId}/members/${memberId}`, authToken, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ role: newRole }),
      });

      if (response.ok) {
        toast.success('Role updated');
        setMembers((prev) =>
          prev.map((m) =>
            m.id === memberId ? { ...m, role: newRole as Member['role'] } : m
          )
        );
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update role');
      }
    } catch {
      toast.error('Failed to update role');
    } finally {
      setActionMemberId(null);
    }
  }

  function renderInviteAccess() {
    if (access.loading && !access.data) {
      return <p className="text-xs text-gray-500">Loading applications…</p>;
    }
    if (!access.data || !inviteAccess) {
      // Central could not list the apps. The invite still works: with no
      // selection Central applies its own default, so nothing is lost.
      return (
        <p className="text-xs text-gray-500" data-testid="invite-access-unavailable">
          {isPersonal
            ? 'Application access will be the household default (Home).'
            : 'Application access will follow the chosen Subrole.'}
        </p>
      );
    }
    const customized = Object.entries(inviteAccess.draft.overrides).some(
      ([appId, list]) => list !== null && appId in inviteAccess.draft.appRoles,
    );
    return (
      <div className="space-y-2">
        <ApplicationAccessEditor
          apps={access.data.assignable.apps}
          catalog={access.data.catalog}
          mainRole={inviteRole}
          value={inviteAccess.draft}
          onChange={(draft) => setInviteAccess({ draft, dirty: true })}
          idPrefix="invite"
        />
        {!isPersonal && !inviteAccess.dirty && (
          <p className="text-xs text-gray-500">
            Untouched, application access follows the chosen Subrole.
          </p>
        )}
        {!isPersonal && customized && inviteTemplate && (
          <p className="text-xs text-amber-700" data-testid="invite-customized-note">
            Customized permissions are sent as an explicit list, so this person will show as
            “Custom” rather than “{inviteTemplate.name}”.
          </p>
        )}
      </div>
    );
  }

  const activeMembers = members.filter((m) => m.type === 'member');
  const pendingInvites = members.filter((m) => m.type === 'invite');

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-8 text-center">
        <div className="animate-spin w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Household Seat Summary — straight from seat enforcement */}
      {isPersonal && householdSeats && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-6">
          <h3 className="text-sm font-medium text-gray-500">Household Seats</h3>
          <p className="text-2xl font-semibold text-gray-900 mt-1" data-testid="household-seats">
            {householdSeats.activeSeats}{' '}
            <span className="text-base font-normal text-gray-500">/ {householdSeats.seatLimit} used</span>
          </p>
          <p className="text-sm text-gray-500 mt-1">
            You, household members and viewers each use a seat. Pending invitations do not.
          </p>
          {householdSeats.isAtLimit && (
            <p role="status" className="text-sm text-amber-700 mt-2">
              All household seats are in use. New invitations can be sent, but they can't be
              accepted until a seat frees up or you upgrade your plan.
            </p>
          )}
        </div>
      )}

      {/* Seat Summary */}
      {seats && !isPersonal && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Team Seats</h3>
              <p className="text-2xl font-semibold text-gray-900 mt-1">
                {seats.current} <span className="text-base font-normal text-gray-500">/ {seats.included} included</span>
              </p>
            </div>
            {seats.additional > 0 && (
              <div className="text-right">
                <p className="text-sm text-gray-500">Additional seats</p>
                <p className="text-lg font-medium text-gray-900">
                  {seats.additional} x ${(seats.perSeatCents / 100).toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">
            {isPersonal ? 'Household Members' : 'Team Members'} ({activeMembers.length})
          </h2>
          {policy.canInvite && (
            <button
              onClick={() => setShowInviteModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
            >
              Invite Member
            </button>
          )}
        </div>
        {!policy.canInvite && (
          <p className="px-6 pt-4 text-sm text-gray-500" data-testid="members-read-only">
            Only workspace owners and admins can manage members.
          </p>
        )}
        {resolvedRole === 'admin' && (
          <p className="px-6 pt-4 text-xs text-gray-500" data-testid="members-owner-only-roles">
            Only the workspace owner can change workspace roles.
          </p>
        )}
        <div className="divide-y divide-gray-100">
          {activeMembers.map((member) => (
            <div key={member.id} className="px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                  {member.avatarUrl ? (
                    <img
                      src={member.avatarUrl}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-sm font-medium text-gray-500">
                      {member.name?.charAt(0).toUpperCase() || member.email?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{member.name || 'Unknown'}</p>
                  <p className="text-sm text-gray-500">{member.email}</p>
                  {member.templateName && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Permissions: {member.templateName}
                    </p>
                  )}
                  {member.role !== 'owner' && member.appRoles !== undefined && (
                    <p className="text-xs text-gray-400 mt-0.5" data-testid={`member-apps-${member.id}`}>
                      {describeApps(member.appRoles, access.appName, member.scopeCountByApp)}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {!policy.canChangeRole(member) ? (
                  <span
                    className={`px-2.5 py-1 rounded-full text-xs font-medium ${roleColors[member.role]}`}
                    data-testid={`member-role-${member.id}`}
                  >
                    {roleLabels[member.role]}
                  </span>
                ) : (
                  <select
                    value={member.role}
                    onChange={(e) =>
                      setPendingConfirm({ kind: 'role', member, newRole: e.target.value as InvitableRole })
                    }
                    disabled={actionMemberId === member.id}
                    aria-label={`Workspace role for ${member.name || member.email}`}
                    className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                    <option value="viewer">Viewer</option>
                  </select>
                )}
                {(policy.canEditAppAccess(member, isPersonal) || policy.canEditPermissions(member)) && (
                  <>
                    {member.appRoles !== undefined && policy.canEditAppAccess(member, isPersonal) && member.role !== 'owner' && (
                      <button
                        onClick={() => setEditingAccess(member)}
                        disabled={actionMemberId === member.id}
                        className="px-2 py-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
                        title="Edit access"
                      >
                        Access
                      </button>
                    )}
                    {/* Capability templates are a business concept; a household has none. */}
                    {!isPersonal && policy.canEditPermissions(member) && (
                      <button
                        onClick={() => setEditingMember(member)}
                        disabled={actionMemberId === member.id}
                        className="p-2 text-gray-400 hover:text-emerald-600 disabled:opacity-50"
                        title="Edit permissions"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </button>
                    )}
                  </>
                )}
                {policy.canRemove(member) && (
                  <button
                    onClick={() => setPendingConfirm({ kind: 'remove', member })}
                    disabled={actionMemberId === member.id}
                    className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Remove member"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
          {activeMembers.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">
              {isPersonal ? 'No household members yet' : 'No team members yet'}
            </div>
          )}
        </div>
      </div>

      {/* Pending Invites */}
      {pendingInvites.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-900/5">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Pending Invites ({pendingInvites.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100">
            {pendingInvites.map((invite) => (
              <div key={invite.inviteId || invite.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{invite.email}</p>
                    <p className="text-sm text-gray-500">
                      Invited as {roleLabels[invite.role]}
                      {invite.templateName && <> &middot; {invite.templateName}</>}
                      {invite.expiresAt && (
                        <> &middot; Expires {new Date(invite.expiresAt).toLocaleDateString()}</>
                      )}
                    </p>
                    {invite.appRoles !== undefined && (
                      <p className="text-xs text-gray-400 mt-0.5" data-testid={`invite-apps-${invite.inviteId}`}>
                        {describeApps(invite.appRoles, access.appName, invite.scopeCountByApp)}
                        {invite.takesSeat ? ' · Takes a seat when accepted' : ' · Does not take a seat'}
                      </p>
                    )}
                  </div>
                </div>
                {policy.canManageInvites && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleResendInvite(invite.inviteId!)}
                      disabled={actionMemberId === invite.inviteId}
                      className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => setPendingConfirm({ kind: 'cancel-invite', member: invite })}
                      disabled={actionMemberId === invite.inviteId}
                      className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {inviteStep === 'permissions'
                  ? 'Set Permissions'
                  : isPersonal
                  ? 'Invite Household Member'
                  : 'Invite Team Member'}
              </h3>
              <button onClick={closeInviteModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {inviteStep === 'details' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder={isPersonal ? 'name@example.com' : 'teammate@company.com'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {policy.inviteRoles.includes('admin') && (
                        <option value="admin">Admin - Can manage members and billing</option>
                      )}
                      <option value="member">
                        {isPersonal ? 'Member - Full access to the household' : 'Member - Full access to workspace'}
                      </option>
                      <option value="viewer">Viewer - Read-only access</option>
                    </select>
                  </div>
                  {isPersonal && householdSeats?.isAtLimit && (
                    <p role="status" className="text-sm text-amber-700">
                      All {householdSeats.seatLimit} household seats are in use. This person can't
                      join until a seat frees up or you upgrade your plan.
                    </p>
                  )}
                  {isPersonal && renderInviteAccess()}
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-4">
                      Choose a permission template for <span className="font-medium">{inviteEmail}</span>
                    </p>
                    <TemplateSelector
                      workspaceId={workspaceId}
                      selectedTemplateId={inviteTemplate?.id || null}
                      onSelect={setInviteTemplate}
                      suggestedRole={inviteRole}
                    />
                  </div>
                  {inviteTemplate && (
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Permission Preview</h4>
                      <TemplatePreview
                        workspaceId={workspaceId}
                        templateId={inviteTemplate.id}
                      />
                    </div>
                  )}
                  <div className="pt-4 border-t border-gray-200">{renderInviteAccess()}</div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              {inviteStep === 'permissions' ? (
                <>
                  <button
                    onClick={() => setInviteStep('details')}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleInvite}
                    disabled={isInviting}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {isInviting ? 'Sending...' : 'Send Invite'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={closeInviteModal}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  {isPersonal ? (
                    <button
                      onClick={handleInvite}
                      disabled={!inviteEmail.trim() || isInviting}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {isInviting ? 'Sending...' : 'Send Invite'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setInviteStep('permissions')}
                      disabled={!inviteEmail.trim()}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      Next: Set Permissions
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {pendingConfirm && (
        <ConfirmMemberAction
          pending={pendingConfirm}
          isPersonal={isPersonal}
          busy={actionMemberId === (pendingConfirm.member.inviteId ?? pendingConfirm.member.id)}
          onCancel={() => setPendingConfirm(null)}
          onConfirm={() => void confirmPending()}
        />
      )}

      {editingAccess && (
        <EditMemberAccessModal
          workspaceId={workspaceId}
          authToken={authToken}
          member={editingAccess}
          access={access}
          callerRole={resolvedRole ?? null}
          callerAppRoles={members.find((m) => m.type === 'member' && !!callerUserId && m.userId === callerUserId)?.appRoles ?? null}
          onClose={() => setEditingAccess(null)}
          onSaved={() => {
            setEditingAccess(null);
            loadData();
          }}
        />
      )}

      {/* Edit Member Permissions Modal */}
      {editingMember && (
        <EditMemberPermissionsModal
          workspaceId={workspaceId}
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSave={(templateId) => handleUpdateMemberTemplate(editingMember.id, templateId)}
          isLoading={actionMemberId === editingMember.id}
        />
      )}
    </div>
  );
}

const ROLE_MEANING: Record<string, string> = {
  admin: 'Admins manage members, app access and billing.',
  member: 'Members use the workspace and its apps.',
  viewer: 'Viewers have read-only access.',
};

/** Confirmation for remove, role change and cancel-invite — each says what will happen. */
function ConfirmMemberAction({
  pending,
  isPersonal,
  busy,
  onCancel,
  onConfirm,
}: {
  pending: PendingConfirm;
  isPersonal: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const who = pending.member.name || pending.member.email;
  const place = isPersonal ? 'household' : 'workspace';
  let title: string;
  let body: string;
  let confirmLabel: string;
  let keepLabel: string;
  if (pending.kind === 'remove') {
    title = `Remove ${who}?`;
    body = `${who} loses access to this ${place} and all of its apps right away, and leaves every team here. You can invite them again later.`;
    confirmLabel = `Remove from ${place}`;
    keepLabel = 'Keep member';
  } else if (pending.kind === 'role') {
    title = `Change ${who}'s role?`;
    body = `${who} becomes ${roleLabels[pending.newRole]} (was ${roleLabels[pending.member.role]}). ${ROLE_MEANING[pending.newRole] ?? ''}`;
    confirmLabel = 'Change role';
    keepLabel = 'Keep current role';
  } else {
    title = `Cancel the invitation to ${pending.member.email}?`;
    body = 'The invitation link stops working. You can send a new invitation later.';
    confirmLabel = 'Cancel invitation';
    keepLabel = 'Keep invitation';
  }
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div role="dialog" aria-modal="true" aria-label={title} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{body}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {keepLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
              pending.kind === 'role' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal for editing a member's permission template
 */
interface EditMemberPermissionsModalProps {
  workspaceId: string;
  member: Member;
  onClose: () => void;
  onSave: (templateId: string | null) => void;
  isLoading: boolean;
}

function EditMemberPermissionsModal({
  workspaceId,
  member,
  onClose,
  onSave,
  isLoading,
}: EditMemberPermissionsModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<CapabilityTemplate | null>(null);
  const [initialTemplateId] = useState(member.templateId);

  // Track if selection has changed
  const hasChanges = selectedTemplate?.id !== initialTemplateId;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Edit Permissions</h3>
            <p className="text-sm text-gray-500">{member.name || member.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Select a permission template for this member
              </p>
              <TemplateSelector
                workspaceId={workspaceId}
                selectedTemplateId={selectedTemplate?.id || member.templateId || null}
                onSelect={setSelectedTemplate}
                suggestedRole={member.role === 'owner' ? undefined : member.role}
              />
            </div>
            {selectedTemplate && (
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Permission Preview</h4>
                <TemplatePreview
                  workspaceId={workspaceId}
                  templateId={selectedTemplate.id}
                />
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(selectedTemplate?.id || null)}
            disabled={isLoading || !hasChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}


/** "Apps: Home (Member) · custom permissions" — names, never raw keys. */
function describeApps(
  appRoles: Record<string, string>,
  appName: (appId: string) => string,
  scopeCountByApp?: Record<string, number>,
): string {
  const ids = Object.keys(appRoles);
  if (ids.length === 0) return 'Apps: none';
  if (ids.includes('*')) return 'Apps: all applications';
  const parts = ids.sort().map((id) => {
    const tier = appRoles[id] ?? '';
    const custom = (scopeCountByApp?.[id] ?? 0) > 0 ? ', customized' : '';
    return `${appName(id)} (${roleLabels[tier] ?? tier}${custom})`;
  });
  return `Apps: ${parts.join(' · ')}`;
}

interface AccessData {
  assignable: AssignableAppsResponse;
  catalog: CatalogApp[];
}

/**
 * Central's assignable-apps contract and access catalog, loaded on first
 * need. Failure leaves `data` null so callers degrade to Central's defaults.
 */
function useAccessData(workspaceId: string, authToken: string) {
  const [data, setData] = useState<AccessData | null>(null);
  const [loading, setLoading] = useState(false);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (!requested) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [appsRes, catRes] = await Promise.all([
          authFetch(`/api/workspaces/${workspaceId}/assignable-apps`, authToken),
          authFetch(`/api/workspaces/${workspaceId}/access-catalog`, authToken),
        ]);
        if (!appsRes?.ok) return;
        const assignable = (await appsRes.json()) as AssignableAppsResponse;
        const catalog = catRes?.ok ? (((await catRes.json()) as { apps?: CatalogApp[] }).apps ?? []) : [];
        if (!cancelled && Array.isArray(assignable.apps)) setData({ assignable, catalog });
      } catch {
        // Degrade: no editor, Central's defaults apply.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [requested, workspaceId, authToken]);

  const names = new Map<string, string>();
  for (const a of data?.assignable.apps ?? []) names.set(a.appId, a.displayName);
  for (const c of data?.catalog ?? []) names.set(c.appId, c.displayName);

  return {
    data,
    loading,
    ensureLoaded: () => setRequested(true),
    appName: (appId: string) =>
      names.get(appId) ?? appId.charAt(0).toUpperCase() + appId.slice(1),
  };
}

type AccessHandle = ReturnType<typeof useAccessData>;

/**
 * Edit an existing member's application access and per-app permissions.
 *
 * Independent of the main role (changed in the list) and of Department/Team.
 * Saves are two Central calls — the full appRoles map, then per-app scope
 * deltas — and only what changed is sent. An app switched off keeps its
 * stored permissions dormant, so switching it back on restores them.
 */
function EditMemberAccessModal({
  workspaceId,
  authToken,
  member,
  access,
  callerRole,
  callerAppRoles,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  authToken: string;
  member: Member;
  access: AccessHandle;
  /** The caller's workspace role and own app roles: Central bounds non-owners by their own tier. */
  callerRole?: string | null;
  callerAppRoles?: Record<string, string> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const storedScopes = member.scopes ?? [];
  const initial: AccessDraft = {
    appRoles: { ...(member.appRoles ?? {}) },
    overrides: {},
  };
  for (const appId of Object.keys(initial.appRoles)) {
    const list = appScopes(storedScopes, appId);
    initial.overrides[appId] = list.length > 0 ? list : null;
  }
  const [draft, setDraft] = useState<AccessDraft>(initial);
  const [saving, setSaving] = useState(false);
  /** Central refused the permissions half (SCOPE_AUTHORITY / OWNER_ACCESS_IMMUTABLE): what it said. */
  const [scopeRefusal, setScopeRefusal] = useState<{ error: string; capabilities: string[] } | null>(null);
  const hasWildcard = '*' in (member.appRoles ?? {});

  useEffect(() => {
    access.ensureLoaded();
  }, []);

  const listedAppIds = (access.data?.assignable.apps ?? []).map((a) => a.appId);
  // Mirror Central's authority rule so Save is never offered for a map it refuses.
  const violations = access.data
    ? appRoleAuthorityViolations(callerRole, callerAppRoles, nextAppRolesFor(member.appRoles, draft.appRoles, listedAppIds))
    : [];
  const appName = (id: string) => access.appName(id);

  async function save() {
    if (violations.length > 0) return;
    setSaving(true);
    setScopeRefusal(null);
    const scopesUrl = `/api/workspaces/${workspaceId}/members/${member.userId}/scopes`;
    const refusalOf = (err: { error?: string; details?: { violations?: Array<{ capability: string }> } }) => ({
      error: err.error || 'Central refused these permissions',
      capabilities: (err.details?.violations ?? []).map((v) => v.capability),
    });
    try {
      // Full intended map: untouched and dormant keys travel unchanged.
      const nextAppRoles = nextAppRolesFor(member.appRoles, draft.appRoles, listedAppIds);
      const appRolesChanged = JSON.stringify(sortKeys(nextAppRoles)) !== JSON.stringify(sortKeys(member.appRoles ?? {}));

      const add: string[] = [];
      const remove: string[] = [];
      for (const id of Object.keys(draft.overrides)) {
        if (!(id in draft.appRoles)) continue; // switched off: permissions stay dormant
        const before = appScopes(storedScopes, id);
        const after = draft.overrides[id] ?? [];
        const d = scopeDelta(before, after);
        add.push(...d.add);
        remove.push(...d.remove);
      }
      const scopesChanged = add.length > 0 || remove.length > 0;

      // Two calls are not one transaction. So when BOTH halves change, ask
      // Central first whether it will accept the permissions half (dryRun:
      // every check, nothing written) — and save nothing if it will not,
      // instead of saving the app roles and then failing on the permissions.
      if (appRolesChanged && scopesChanged) {
        const pre = await authFetch(scopesUrl, authToken, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ add, remove, dryRun: true }),
        });
        if (!pre.ok) {
          const err = await pre.json().catch(() => ({}));
          setScopeRefusal(refusalOf(err));
          toast.error(err.error || 'Failed to update permissions');
          return;
        }
      }

      if (appRolesChanged) {
        const res = await authFetch(`/api/workspaces/${workspaceId}/members/${member.userId}/app-roles`, authToken, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ appRoles: nextAppRoles }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          toast.error(err.error || 'Failed to update application access');
          return;
        }
      }

      if (scopesChanged) {
        const res = await authFetch(scopesUrl, authToken, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ add, remove, reason: 'Access edited in Central settings' }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setScopeRefusal(refusalOf(err));
          // Only reachable after a passed preflight if something changed in
          // between; say exactly which half landed rather than "failed".
          toast.error(
            appRolesChanged
              ? `Application access was saved, but permissions were not: ${err.error || 'Central refused them'}`
              : err.error || 'Failed to update permissions',
          );
          return;
        }
      }
      toast.success('Access updated');
      onSaved();
    } catch {
      toast.error('Failed to update access');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Edit Access</h3>
          <p className="text-sm text-gray-500">
            {member.name || member.email} · {roleLabels[member.role]}
          </p>
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {scopeRefusal && (
            <div role="alert" className="mb-3 rounded-lg bg-red-50 p-3 text-xs text-red-700 ring-1 ring-red-200" data-testid="scope-authority">
              {scopeRefusal.error}
              {scopeRefusal.capabilities.length > 0 && (
                <ul className="mt-1 list-disc pl-4">
                  {scopeRefusal.capabilities.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {violations.length > 0 && (
            <div role="alert" className="mb-3 rounded-lg bg-red-50 p-3 text-xs text-red-700 ring-1 ring-red-200" data-testid="app-role-authority">
              You can only grant access up to your own level:
              <ul className="mt-1 list-disc pl-4">
                {violations.map((v) => (
                  <li key={v.appId}>
                    {appName(v.appId)}: {v.required} needs your own access to be at least {v.required} (you have{' '}
                    {v.callerHas ?? 'none'})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {hasWildcard ? (
            <p className="text-sm text-gray-600">This person has access to all applications.</p>
          ) : access.loading && !access.data ? (
            <p className="text-sm text-gray-500">Loading applications…</p>
          ) : (
            <ApplicationAccessEditor
              apps={access.data?.assignable.apps ?? []}
              catalog={access.data?.catalog ?? []}
              mainRole={member.role as MainRole}
              value={draft}
              onChange={setDraft}
              readOnly={saving}
              unavailableReason={access.data ? null : 'Applications could not be loaded. Try again shortly.'}
              idPrefix={`edit-${member.userId}`}
            />
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900">
            Cancel
          </button>
          <button
            onClick={() => void save()}
            disabled={saving || hasWildcard || !access.data || violations.length > 0}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Access'}
          </button>
        </div>
      </div>
    </div>
  );
}

function sortKeys(o: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(o).sort(([a], [b]) => a.localeCompare(b)));
}
